import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Upload, FileText, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const CATEGORIES = ["Rehearsal", "Performance", "Teaching", "Meeting", "Travel", "Personal", "Other"];

function fmtRange(start, end) {
  if (!start && !end) return "All day";
  if (start && end) return `${start}–${end}`;
  return start || end || "All day";
}

function prettyDate(s) {
  if (!s) return "";
  const d = new Date(s + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

export default function DiaryImport() {
  const nav = useNavigate();
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [events, setEvents] = useState([]);
  const [series, setSeries] = useState([]);
  const [parseSummary, setParseSummary] = useState(null);
  const [committing, setCommitting] = useState(false);

  async function onParse(e) {
    e?.preventDefault?.();
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".ics")) {
      toast.error("Please choose a .ics file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File is larger than 2 MB. Please split the export.");
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/diary/import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatchId(data.batch_id);
      setEvents((data.events || []).map((ev) => ({ ...ev, selected: !ev.is_duplicate })));
      setSeries((data.series || []).map((s) => ({ ...s, selected: !s.is_duplicate })));
      setParseSummary({
        parsed: data.parsed_count,
        duplicates: data.duplicate_count,
        events: data.event_count,
        series: data.series_count,
      });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not parse file.";
      toast.error(msg);
    } finally {
      setParsing(false);
    }
  }

  function toggle(idx) {
    setEvents((prev) => prev.map((ev, i) => (i === idx ? { ...ev, selected: !ev.selected } : ev)));
  }

  function toggleSeries(idx) {
    setSeries((prev) => prev.map((s, i) => (i === idx ? { ...s, selected: !s.selected } : s)));
  }

  function setCategory(idx, category) {
    setEvents((prev) => prev.map((ev, i) => (i === idx ? { ...ev, category } : ev)));
  }

  function setSeriesCategory(idx, category) {
    setSeries((prev) => prev.map((s, i) => (i === idx ? { ...s, category } : s)));
  }

  const selectedEventCount = events.filter((e) => e.selected && !e.is_duplicate).length;
  const selectedSeriesCount = series.filter((s) => s.selected && !s.is_duplicate).length;
  const selectedCount = selectedEventCount + selectedSeriesCount;

  async function onCommit() {
    if (!batchId) return;
    if (selectedCount === 0) {
      toast.error("Select at least one event or series to import.");
      return;
    }
    setCommitting(true);
    try {
      const payload = {
        events: events.map((ev) => ({
          ics_uid: ev.ics_uid,
          category: ev.category,
          selected: !!ev.selected && !ev.is_duplicate,
          kind: "event",
        })),
        series: series.map((s) => ({
          ics_uid: s.ics_uid,
          category: s.category,
          selected: !!s.selected && !s.is_duplicate,
          kind: "series",
        })),
      };
      const { data } = await api.post(`/diary/import/${batchId}/commit`, payload);
      const parts = [
        `${data.imported_count} events`,
        `${data.imported_series_count} series`,
        `${data.duplicate_count} duplicates skipped`,
      ];
      toast.success(parts.join(" · "));
      nav("/diary");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save events.";
      toast.error(msg);
    } finally {
      setCommitting(false);
    }
  }

  // ─── State 1: upload ────────────────────────────────────────────────
  if (!batchId) {
    return (
      <div className="space-y-6" data-testid="diary-import-upload">
        <div className="flex items-center gap-3">
          <Link to="/diary" className="text-ink-500 hover:text-ink-900" data-testid="back-to-diary">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <div className="label-overline">Diary</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Import from another calendar</h1>
          </div>
        </div>

        <div className="card p-6 max-w-2xl">
          <p className="text-ink-700 text-sm mb-4">
            Bring in your existing diary from Google Calendar, Apple Calendar,
            Outlook or any app that exports <code className="bg-ivory-100 px-1 rounded">.ics</code>.
            You&apos;ll get a chance to review every event before anything is saved.
          </p>
          <ul className="text-ink-600 text-sm space-y-1.5 mb-6 list-disc pl-5">
            <li>Imported events are <strong>private to you</strong>. Bookers only ever see your availability status, never the details.</li>
            <li>Everything defaults to <strong>Unavailable</strong>. You can change any event on the Diary page after import.</li>
            <li>Maximum <strong>2 MB</strong> file, <strong>500 events</strong>.</li>
          </ul>

          <form onSubmit={onParse} className="space-y-4">
            <label className="block">
              <span className="text-ink-800 text-sm font-medium mb-2 block">Choose .ics file</span>
              <input
                type="file"
                accept=".ics,text/calendar"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-ink-800 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-sage-500 file:text-white hover:file:bg-sage-600 file:cursor-pointer"
                data-testid="ics-file-input"
              />
              {file && (
                <div className="mt-2 text-xs text-ink-500 flex items-center gap-1.5">
                  <FileText size={14} /> {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
            </label>

            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={!file || parsing}
                className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="parse-btn"
              >
                {parsing ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {parsing ? "Parsing..." : "Parse & review"}
              </button>
              <Link to="/diary" className="btn-secondary text-sm">Cancel</Link>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ─── State 2: review ────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="diary-import-review">
      <div className="flex items-center gap-3">
        <Link to="/diary" className="text-ink-500 hover:text-ink-900">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <div className="label-overline">Diary import</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Review before saving</h1>
        </div>
      </div>

      <div className="card p-4 flex flex-wrap gap-4 text-sm items-center">
        <div>
          <span className="text-ink-500">Parsed:</span>{" "}
          <span className="font-semibold text-ink-900" data-testid="parsed-count">{parseSummary?.parsed || 0}</span>
        </div>
        <div>
          <span className="text-ink-500">Series:</span>{" "}
          <span className="font-semibold text-ink-900" data-testid="series-count">{parseSummary?.series || 0}</span>
        </div>
        <div>
          <span className="text-ink-500">Events:</span>{" "}
          <span className="font-semibold text-ink-900" data-testid="event-count">{parseSummary?.events || 0}</span>
        </div>
        <div>
          <span className="text-ink-500">Duplicates:</span>{" "}
          <span className="font-semibold text-ink-900" data-testid="dup-count">{parseSummary?.duplicates || 0}</span>
        </div>
        <div>
          <span className="text-ink-500">Selected:</span>{" "}
          <span className="font-semibold text-sage-600" data-testid="selected-count">{selectedCount}</span>
        </div>
      </div>

      {series.length > 0 && (
        <div className="card overflow-hidden" data-testid="series-review-block">
          <div className="px-4 py-2 bg-sage-50 text-sage-700 text-xs uppercase tracking-wide">
            Recurring series — stored as a single rule, expanded on the fly
          </div>
          <table className="w-full text-sm">
            <thead className="bg-ivory-100 text-ink-700 text-xs uppercase tracking-wide">
              <tr>
                <th className="p-3 text-left w-10"></th>
                <th className="p-3 text-left">Series</th>
                <th className="p-3 text-left">Occurrences</th>
                <th className="p-3 text-left">Category</th>
              </tr>
            </thead>
            <tbody>
              {series.map((sr, idx) => (
                <tr
                  key={sr.ics_uid + "-series-" + idx}
                  className={`border-t border-ivory-200 ${sr.is_duplicate ? "bg-ivory-50 opacity-70" : ""}`}
                  data-testid={`series-row-${idx}`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={sr.selected}
                      onChange={() => toggleSeries(idx)}
                      disabled={sr.is_duplicate}
                      className="w-4 h-4 accent-sage-500"
                      data-testid={`series-include-${idx}`}
                    />
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-ink-900">{sr.title}</div>
                    <div className="text-xs text-ink-500">
                      {sr.recurrence?.freq === "WEEKLY" && sr.recurrence?.byweekday?.length
                        ? `Weekly · ${sr.recurrence.byweekday.join(", ")}`
                        : sr.recurrence?.freq === "DAILY"
                          ? `Daily · every ${sr.recurrence.interval || 1} day(s)`
                          : `${sr.recurrence?.freq || "Recurring"}`}
                      {sr.recurrence?.tz && ` · ${sr.recurrence.tz}`}
                    </div>
                    {sr.is_duplicate && (
                      <div className="text-xs text-terracotta-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {sr.duplicate_reason || "already imported"}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-ink-600 text-xs">
                    <div>{sr.occurrence_count} total</div>
                    {sr.occurrences?.some((o) => o.is_exdate) && (
                      <div className="text-terracotta-600">
                        {sr.occurrences.filter((o) => o.is_exdate).length} cancelled (EXDATE)
                      </div>
                    )}
                    {sr.occurrences?.some((o) => o.is_modified) && (
                      <div className="text-amber-deep">
                        {sr.occurrences.filter((o) => o.is_modified).length} modified
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <select
                      value={sr.category}
                      onChange={(e) => setSeriesCategory(idx, e.target.value)}
                      disabled={sr.is_duplicate}
                      className="border border-ivory-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                      data-testid={`series-category-${idx}`}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="max-h-[600px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-ivory-100 text-ink-700 text-xs uppercase tracking-wide">
              <tr>
                <th className="p-3 text-left w-10"></th>
                <th className="p-3 text-left">Date · time</th>
                <th className="p-3 text-left">Title</th>
                <th className="p-3 text-left">Category</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, idx) => (
                <tr
                  key={ev.ics_uid + "#" + idx}
                  className={`border-t border-ivory-200 ${ev.is_duplicate ? "bg-ivory-50 opacity-70" : ""}`}
                  data-testid={`event-row-${idx}`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={ev.selected}
                      onChange={() => toggle(idx)}
                      disabled={ev.is_duplicate}
                      className="w-4 h-4 accent-sage-500"
                      data-testid={`include-checkbox-${idx}`}
                    />
                  </td>
                  <td className="p-3 text-ink-800 whitespace-nowrap">
                    <div className="font-medium">{prettyDate(ev.date)}</div>
                    <div className="text-xs text-ink-500">{fmtRange(ev.start_time, ev.end_time)}</div>
                  </td>
                  <td className="p-3">
                    <div className="text-ink-900">{ev.title}</div>
                    {ev.venue && <div className="text-xs text-ink-500">{ev.venue}</div>}
                    {ev.is_duplicate && (
                      <div className="text-xs text-terracotta-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} /> {ev.duplicate_reason || "already in diary"}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <select
                      value={ev.category}
                      onChange={(e) => setCategory(idx, e.target.value)}
                      disabled={ev.is_duplicate}
                      className="border border-ivory-200 rounded-lg px-2 py-1.5 text-sm bg-white"
                      data-testid={`category-select-${idx}`}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end gap-2 pt-2">
        <Link to="/diary" className="btn-secondary text-sm">Cancel</Link>
        <button
          onClick={onCommit}
          disabled={committing || selectedCount === 0}
          className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="commit-btn"
        >
          {committing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
          {committing ? "Saving..." : `Save ${selectedCount} event${selectedCount === 1 ? "" : "s"}`}
        </button>
      </div>
    </div>
  );
}
